import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { postLogDetails } from "../../../Services/APIs/appDetails"
import teethSides from "../../../Utils/teethSideNames"
import crop from "../../../Utils/crop"
import { encrypt } from "../../../Utils/hmac"
import dataURItoBlob from "../../../Utils/dataUriToBlob"
import { imageUpload } from "../../../Services/APIs/scan"
import { addImages } from "../../../Store/fullScan"
import {
    FailedError,
    MouthFarAwayError,
    BlurDetectedError,
    InvalidImageError,
    GeneralError
} from "../ErrorPopups"
import { steps } from "./steps"

const Preview = (props) => {
    const { image, setCamera, activeImageSide, setDemo, setImageSide } = props
    const dispatch = useDispatch()

    const [reshoot, setReshoot] = useState(false)
    const [errorType, setImageError] = useState('')
    const [isLoading, setLoading] = useState(false)
    const uid = JSON.parse(localStorage.getItem("usertpi") || '{}').usertpi
    const app = JSON.parse(localStorage.getItem("companyBucket") || "{}").companyBucket

    useEffect(() => {
        postLogDetails({
            uid,
            app,
            payLoad: `Previewing ${teethSides[activeImageSide]} teeth`
        })
    }, [])

    const handleReshoot = () => {
        setCamera(true)
        postLogDetails({
            uid,
            app,
            payLoad: `reshooting ${teethSides[activeImageSide]} teeth`
        })
    }

    const next = async () => {
        const rotate = activeImageSide === 4
        const croppedImage = await crop(image, 3 / 2, rotate).then(canvas => {
            return canvas.toDataURL("image/jpeg", 0.75);
        })
        postLogDetails({
            uid,
            app,
            payLoad: `Next clicked for ${teethSides[activeImageSide]} teeth`
        })
        await upload(croppedImage)
    }

    const upload = async (dataUri) => {
        const data = {
            uid: JSON.parse(localStorage.getItem('usertpi') || "{}").usertpi,
            folderName: JSON.parse(localStorage.getItem('usertpi') || "{}").usertpi,
            company: JSON.parse(localStorage.getItem("companyBucket") || "{}").companyBucket,
            teethSide: `${teethSides[activeImageSide]} teeth`,
        }
        const encrypted = encrypt(data)
        const blob = dataURItoBlob(dataUri)
        const file = new File([blob], "report_image.jpeg", { type: "image/jpeg" });
        const formData = new FormData();
        formData.append("report_image", file);
        formData.append("_n", encrypted);
        setLoading(true)
        setReshoot(false)
        await imageUpload({ formData }).then((response) => {
            setLoading(false)

            const required = {
                title: steps[activeImageSide].title,
                dataUri: URL.createObjectURL(blob),
                file: response.data.data?.imageName,
                isReshoot: response.data.data?.is_reshoot,
                mouth_detection_message: response.data.data?.mouth_detection_message
            }
            if (required.isReshoot) {
                postLogDetails({
                    uid,
                    app,
                    payLoad: `Reshoot error: ${required.mouth_detection_message}`
                })
                setReshoot(true)
                if (response.data.data?.mouth_detection_message === "mouth far away") {
                    setImageError("mouth far away")
                } else if (response.data.data?.mouth_detection_message === "blur detected") {
                    setImageError("blur detected")
                }
                else {
                    setImageError("general")
                }
            } else {
                dispatch(addImages(required))
                setImageSide(activeImageSide + 1)
                setDemo(true)
            }

        }).catch((error) => {
            console.log(error)
            postLogDetails({
                uid,
                app,
                payLoad: `Upload failed for ${teethSides[activeImageSide]} teeth: ${error.message || error}`
            })
            setLoading(false)
            setReshoot(true)
            setImageError("failed")
        })


    }

    return (
        <div className="h-screen">
            <div className="relative top-0 h-full w-full">
                <img
                    src={image}
                    alt="preview"
                    className={`w-full object-cover h-full ${activeImageSide === 4 ? 'transform rotate-180' : ''}`}
                />
            </div>

            <div className="z-10 absolute bottom-5 w-full flex justify-evenly">
                <button
                    className=" block w-40 h-12 bg-white text-black font-semibold rounded"
                    onClick={handleReshoot}>
                    <span className=" text-peri-blue text-lg font-cera-pro-medium"> ပြန်ရိုက်မည် </span>
                </button>
                <button
                    className=" block w-40  h-12 bg-white text-black font-semibold rounded"
                    onClick={next}
                    disabled={isLoading}
                >
                    {
                        isLoading ? <div className="loader-container">
                            <div className="loader dark-loader"></div>
                        </div> : <span className=" text-peri-blue text-xl font-cera-pro-medium"> ရှေ့သို့ </span>
                    }
                </button>
            </div>

            {/* Error Popups */}
            {reshoot && errorType === "mouth far away" && (
                <div className="h-full z-20 absolute w-full top-0 bg-black/[0.3] backdrop-blur-sm flex justify-center items-center">
                    <MouthFarAwayError
                        onClose={() => setReshoot(false)}
                        onRetake={() => {
                            setReshoot(false);
                            setCamera(true);
                        }}
                    />
                </div>
            )}

            {reshoot && errorType === "blur detected" && (
                <div className="h-full z-20 absolute w-full top-0 bg-black/[0.3] backdrop-blur-sm flex justify-center items-center">
                    <BlurDetectedError
                        onClose={() => setReshoot(false)}
                        onRetake={() => {
                            setReshoot(false);
                            setCamera(true);
                        }}
                        onViewReference={() => setDemo(true)}
                    />
                </div>
            )}

            {reshoot && errorType === "failed" && (
                <div className="h-full z-20 absolute w-full top-0 bg-black/[0.3] backdrop-blur-sm flex justify-center items-center">
                    <FailedError
                        onClose={() => setReshoot(false)}
                    />
                </div>
            )}

            {/* Handle invalid image error - needs special case for poor lighting */}
            {reshoot && errorType === "general" && (
                <div className="h-full z-20 absolute w-full top-0 bg-black/[0.3] backdrop-blur-sm flex justify-center items-center">
                    <InvalidImageError
                        onClose={() => setReshoot(false)}
                        onRetake={() => {
                            setReshoot(false);
                            setCamera(true);
                        }}
                        onViewReference={() => setDemo(true)}
                    />
                </div>
            )}

        </div>
    )
}

export default Preview;